import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight, Sparkles, Swords, Shield, Crosshair, Grid2x2, Orbit } from "lucide-react";
import MainLayout from "@/layouts/MainLayout";
import PokemonTypeTag from "@/components/PokemonTypeTag";
import { cn } from "@/lib/utils";
import {
  POKEMON_TYPES,
  PokemonTypeName,
  getTypeEffectiveness,
} from "@/constants/pokemonTypeChart";
import { useLanguage } from "@/services/languageService";

type FocusCell = {
  attack: PokemonTypeName;
  defense: PokemonTypeName;
};

type ChartViewMode = "matrix" | "radar";

type RadarPointerState = {
  x: number;
  y: number;
  active: boolean;
};

const PAGE_COPY = {
  en: {
    title: "Type Chart",
    subtitle:
      "A cleaner type chart with a quick matchup analyzer. Pick any attack and defense combination, then use the matrix to inspect the full matchup space.",
    attackLabel: "Attack Type",
    defenseLabel: "Primary Defense",
    secondaryLabel: "Secondary Defense",
    secondaryPlaceholder: "No secondary type",
    resultTitle: "Live Matchup",
    matrixTitle: "Interactive Matrix",
    matrixBody:
      "Hover to scan a full row and column. Click a cell to pin it and compare the exact multiplier.",
    analyzerTitle: "Quick Analyzer",
    analyzerBody:
      "Use the selector first, then refine inside the matrix. The two views stay in sync.",
    strongTitle: "Super Effective",
    weakTitle: "Not Very Effective",
    immuneTitle: "No Effect",
    neutralTitle: "Neutral Matchups",
    legendStrong: "2x or higher",
    legendWeak: "0.5x or lower",
    legendImmune: "0x immunity",
    attackHeader: "Atk",
    defenseHeader: "Def",
    pinnedLabel: "Pinned matchup",
    hoverHint: "Hover any cell to preview row and column focus.",
    matrixView: "Matrix",
    radarView: "Radar",
    radarTitle: "Radar Matchup Map",
    radarBody:
      "The current attack type stays in the center. Nearby rings separate immunity, resistance, neutral hits, and strong hits.",
    radarStrongRing: "Strong ring",
    radarNeutralRing: "Neutral ring",
    radarWeakRing: "Resist ring",
    radarImmuneRing: "Immune ring",
    radarHoverHint: "Hover a node to inspect, click to lock it into the matchup result.",
    radarFocusTitle: "Focused Defense",
    radarCurrentAttack: "Current attack",
    radarMultiplier: "Multiplier",
    radarInteraction: "Interaction",
    radarInteractionPinned: "Pinned to analyzer",
    radarInteractionPreview: "Preview only",
    radarLegendStrongText: "2x / 4x",
    radarLegendNeutralText: "1x",
    radarLegendWeakText: "0.5x / 0.25x",
    radarLegendImmuneText: "0x",
  },
  "zh-CN": {
    title: "属性相克表",
    subtitle:
      "把完整相克矩阵和快速分析器放在同一页。先选攻击方与防御方，再用矩阵查看整张属性关系图。",
    attackLabel: "攻击属性",
    defenseLabel: "主防御属性",
    secondaryLabel: "副防御属性",
    secondaryPlaceholder: "无第二属性",
    resultTitle: "当前对局",
    matrixTitle: "交互矩阵",
    matrixBody: "悬停时同时查看整行与整列，点击单元格可以固定当前相克关系。",
    analyzerTitle: "快速分析器",
    analyzerBody: "先用选择器快速判断，再到矩阵里横向比较，两个区域会同步联动。",
    strongTitle: "效果绝佳",
    weakTitle: "效果不佳",
    immuneTitle: "没有效果",
    neutralTitle: "等倍范围",
    legendStrong: "2 倍或更高",
    legendWeak: "0.5 倍或更低",
    legendImmune: "0 倍无效",
    attackHeader: "攻",
    defenseHeader: "防",
    pinnedLabel: "已固定关系",
    hoverHint: "悬停任意格子即可预览该攻击属性与防御属性的交叉关系。",
    matrixView: "矩阵",
    radarView: "雷达",
    radarTitle: "雷达式相性地图",
    radarBody: "当前攻击属性位于中心，不同环层分别表示无效、减半、等倍与克制关系。",
    radarStrongRing: "克制层",
    radarNeutralRing: "等倍层",
    radarWeakRing: "抗性层",
    radarImmuneRing: "无效层",
    radarHoverHint: "悬停节点查看详情，点击后会把该属性固定到当前对局。",
    radarFocusTitle: "当前焦点",
    radarCurrentAttack: "当前攻击",
    radarMultiplier: "倍率",
    radarInteraction: "交互状态",
    radarInteractionPinned: "已固定到分析器",
    radarInteractionPreview: "仅预览",
    radarLegendStrongText: "2x / 4x",
    radarLegendNeutralText: "1x",
    radarLegendWeakText: "0.5x / 0.25x",
    radarLegendImmuneText: "0x",
  },
} as const;

const MULTIPLIER_STYLES: Record<number, string> = {
  0: "bg-slate-900 text-white border-slate-900 shadow-[0_18px_40px_-28px_rgba(15,23,42,0.75)]",
  0.25: "bg-amber-50 text-amber-700 border-amber-200",
  0.5: "bg-amber-50 text-amber-700 border-amber-200",
  1: "bg-white/90 text-slate-500 border-slate-200",
  2: "bg-sky-50 text-sky-700 border-sky-200 shadow-[0_18px_35px_-28px_rgba(12,96,179,0.5)]",
  4: "bg-sky-100 text-sky-800 border-sky-300 shadow-[0_18px_35px_-24px_rgba(12,96,179,0.6)]",
};

const formatMultiplier = (multiplier: number) => {
  if (Number.isInteger(multiplier)) {
    return `${multiplier}x`;
  }

  return `${multiplier}x`;
};

const getMultiplierTone = (multiplier: number) =>
  MULTIPLIER_STYLES[multiplier] || MULTIPLIER_STYLES[1];

const RADAR_RINGS = {
  strong: 220,
  neutral: 170,
  weak: 120,
  immune: 75,
} as const;

const RADAR_RING_SEQUENCE = [
  { key: "strong", radius: RADAR_RINGS.strong },
  { key: "neutral", radius: RADAR_RINGS.neutral },
  { key: "weak", radius: RADAR_RINGS.weak },
  { key: "immune", radius: RADAR_RINGS.immune },
] as const;

const getMultiplierBucket = (multiplier: number) => {
  if (multiplier === 0) return "immune";
  if (multiplier > 1) return "strong";
  if (multiplier < 1) return "weak";
  return "neutral";
};

const TypeChartPage = () => {
  const { currentLanguage, t } = useLanguage();
  const copy = PAGE_COPY[currentLanguage as keyof typeof PAGE_COPY] || PAGE_COPY.en;
  const [selectedAttack, setSelectedAttack] = useState<PokemonTypeName>("fire");
  const [selectedDefense, setSelectedDefense] = useState<PokemonTypeName>("grass");
  const [selectedSecondaryDefense, setSelectedSecondaryDefense] = useState<PokemonTypeName | "none">("none");
  const [hoveredCell, setHoveredCell] = useState<FocusCell | null>(null);
  const [pinnedCell, setPinnedCell] = useState<FocusCell | null>(null);
  const [viewMode, setViewMode] = useState<ChartViewMode>("matrix");
  const [hoveredRadarType, setHoveredRadarType] = useState<PokemonTypeName | null>(null);
  const [radarAnimationSeed, setRadarAnimationSeed] = useState(0);
  const [radarPointer, setRadarPointer] = useState<RadarPointerState>({ x: 0, y: 0, active: false });
  const radarCanvasRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    document.title = `${copy.title} | ${t("subFooterRandomPokemonGeneratorLink")}`;

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content", copy.subtitle);
    }
  }, [copy, t]);

  const activeCell = pinnedCell || hoveredCell || { attack: selectedAttack, defense: selectedDefense };

  useEffect(() => {
    if (!pinnedCell) {
      return;
    }

    setSelectedAttack(pinnedCell.attack);
    setSelectedDefense(pinnedCell.defense);
    setSelectedSecondaryDefense("none");
  }, [pinnedCell]);

  useEffect(() => {
    setRadarAnimationSeed((value) => value + 1);
  }, [selectedAttack]);

  const combinedMultiplier = useMemo(() => {
    const primary = getTypeEffectiveness(selectedAttack, selectedDefense);
    const secondary =
      selectedSecondaryDefense === "none" ? 1 : getTypeEffectiveness(selectedAttack, selectedSecondaryDefense);
    return primary * secondary;
  }, [selectedAttack, selectedDefense, selectedSecondaryDefense]);

  const matchupBuckets = useMemo(() => {
    const immune: PokemonTypeName[] = [];
    const weak: PokemonTypeName[] = [];
    const neutral: PokemonTypeName[] = [];
    const strong: PokemonTypeName[] = [];

    POKEMON_TYPES.forEach((type) => {
      const multiplier = getTypeEffectiveness(selectedAttack, type);
      if (multiplier === 0) {
        immune.push(type);
      } else if (multiplier > 1) {
        strong.push(type);
      } else if (multiplier < 1) {
        weak.push(type);
      } else {
        neutral.push(type);
      }
    });

    return { immune, weak, neutral, strong };
  }, [selectedAttack]);

  const resultSentence = `${t(selectedAttack)} ${copy.attackHeader} ${t(selectedDefense)}${
    selectedSecondaryDefense === "none" ? "" : ` / ${t(selectedSecondaryDefense)}`
  } ${copy.defenseHeader} = ${formatMultiplier(combinedMultiplier)}`;

  const radarNodes = useMemo(() => {
    return POKEMON_TYPES.map((type, index) => {
      const multiplier = getTypeEffectiveness(selectedAttack, type);
      const angle = ((Math.PI * 2) / POKEMON_TYPES.length) * index - Math.PI / 2;
      const bucket = getMultiplierBucket(multiplier);
      const ring =
        bucket === "immune"
          ? RADAR_RINGS.immune
          : bucket === "strong"
            ? RADAR_RINGS.strong
            : bucket === "weak"
              ? RADAR_RINGS.weak
              : RADAR_RINGS.neutral;

      return {
        type,
        multiplier,
        bucket,
        x: Math.cos(angle) * ring,
        y: Math.sin(angle) * ring,
      };
    });
  }, [selectedAttack]);

  const focusedRadarType = hoveredRadarType || selectedDefense;
  const focusedRadarNode =
    radarNodes.find((node) => node.type === focusedRadarType) || radarNodes[0];
  const focusedRadarBucket = focusedRadarNode ? getMultiplierBucket(focusedRadarNode.multiplier) : "neutral";

  const highlightedRing = useMemo(() => {
    if (!radarPointer.active) {
      return focusedRadarBucket;
    }

    const pointerRadius = Math.sqrt(radarPointer.x * radarPointer.x + radarPointer.y * radarPointer.y);
    return RADAR_RING_SEQUENCE.reduce<(typeof RADAR_RING_SEQUENCE)[number]["key"]>((closestKey, ring) => {
      const currentDistance = Math.abs(pointerRadius - ring.radius);
      const bestDistance =
        Math.abs(
          pointerRadius -
            RADAR_RING_SEQUENCE.find((candidate) => candidate.key === closestKey)!.radius
        );

      return currentDistance < bestDistance ? ring.key : closestKey;
    }, "neutral");
  }, [focusedRadarBucket, radarPointer]);

  const radarStageTransform = radarPointer.active
    ? `translate(${(radarPointer.x / 240) * 4}px, ${(radarPointer.y / 240) * 4}px)`
    : "translate(0px, 0px)";

  const handleRadarPointerMove = (event: React.MouseEvent<HTMLDivElement>) => {
    const bounds = radarCanvasRef.current?.getBoundingClientRect();
    if (!bounds) return;

    const relativeX = event.clientX - bounds.left - bounds.width / 2;
    const relativeY = event.clientY - bounds.top - bounds.height / 2;
    const scale = 600 / Math.min(bounds.width, bounds.height);

    setRadarPointer({
      x: relativeX * scale,
      y: relativeY * scale,
      active: true,
    });
  };

  const handleRadarPointerLeave = () => {
    setRadarPointer({ x: 0, y: 0, active: false });
    setHoveredRadarType(null);
  };

  return (
    <MainLayout>
      <div className="type-chart-page relative overflow-hidden rounded-[2rem] bg-gradient-to-b from-white via-blue-50 to-pokemon-red/5 px-4 py-8 sm:px-6 lg:px-8">
        {/* 宝可梦风格装饰光晕 */}
        <div className="pointer-events-none absolute -left-16 top-16 h-56 w-56 rounded-full bg-pokemon-red/10 blur-3xl" />
        <div className="pointer-events-none absolute -right-12 top-28 h-48 w-48 rounded-full bg-pokemon-yellow/10 blur-3xl" />
        <div className="pointer-events-none absolute left-1/3 bottom-20 h-40 w-40 rounded-full bg-pokemon-blue/10 blur-3xl" />

        <div className="relative mx-auto max-w-7xl space-y-8">
          <section className="type-chart-fade-up grid gap-6 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]">
            <div className="space-y-5">
              <div className="inline-flex items-center gap-2 rounded-full border border-pokemon-blue/15 bg-white/85 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.24em] text-pokemon-blue shadow-sm">
                <Sparkles className="h-3.5 w-3.5" />
                <span>{t("pokedexEyebrow")}</span>
              </div>

              <div className="space-y-3">
                <h1 className="text-4xl font-black tracking-tight text-slate-900 sm:text-5xl">
                  {copy.title}
                </h1>
                <p className="max-w-2xl text-sm leading-7 text-slate-600 sm:text-base">{copy.subtitle}</p>
              </div>

              <div className="grid gap-3 sm:grid-cols-3">
                <div className="rounded-[1.5rem] border border-white/80 bg-white/80 p-4 shadow-[0_24px_50px_-42px_rgba(15,23,42,0.35)] backdrop-blur-sm">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400">
                    {copy.legendStrong}
                  </p>
                  <p className="mt-2 text-lg font-black text-slate-900">{matchupBuckets.strong.length}</p>
                </div>
                <div className="rounded-[1.5rem] border border-white/80 bg-white/80 p-4 shadow-[0_24px_50px_-42px_rgba(15,23,42,0.35)] backdrop-blur-sm">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400">
                    {copy.legendWeak}
                  </p>
                  <p className="mt-2 text-lg font-black text-slate-900">{matchupBuckets.weak.length}</p>
                </div>
                <div className="rounded-[1.5rem] border border-white/80 bg-white/80 p-4 shadow-[0_24px_50px_-42px_rgba(15,23,42,0.35)] backdrop-blur-sm">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400">
                    {copy.legendImmune}
                  </p>
                  <p className="mt-2 text-lg font-black text-slate-900">{matchupBuckets.immune.length}</p>
                </div>
              </div>
            </div>

            <div className="type-chart-fade-up-delayed rounded-[1.75rem] border border-white/80 bg-white/88 p-5 shadow-[0_36px_80px_-46px_rgba(15,23,42,0.38)] backdrop-blur-sm">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-pokemon-blue">
                    {copy.resultTitle}
                  </p>
                  <h2 className="mt-2 text-2xl font-black text-slate-900">{formatMultiplier(combinedMultiplier)}</h2>
                </div>
                {pinnedCell ? (
                  <button
                    type="button"
                    onClick={() => setPinnedCell(null)}
                    className="rounded-full border border-pokemon-blue/15 bg-pokemon-blue/5 px-3 py-1 text-xs font-semibold text-pokemon-blue transition hover:bg-pokemon-blue/10"
                  >
                    {copy.pinnedLabel}
                  </button>
                ) : null}
              </div>

              <div className="mt-5 flex flex-wrap items-center gap-2 text-sm text-slate-600">
                <PokemonTypeTag type={selectedAttack} className="rounded-full px-3 py-1 text-sm shadow-sm" />
                <ArrowRight className="h-4 w-4 text-slate-400" />
                <PokemonTypeTag type={selectedDefense} className="rounded-full px-3 py-1 text-sm shadow-sm" />
                {selectedSecondaryDefense !== "none" && (
                  <PokemonTypeTag
                    type={selectedSecondaryDefense}
                    className="rounded-full px-3 py-1 text-sm shadow-sm"
                  />
                )}
              </div>

              <p className="mt-4 text-sm leading-7 text-slate-600">{resultSentence}</p>

              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                <div className="rounded-[1.25rem] border border-sky-100 bg-sky-50/70 p-4">
                  <div className="flex items-center gap-2 text-sky-700">
                    <Swords className="h-4 w-4" />
                    <h3 className="text-sm font-bold">{copy.strongTitle}</h3>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {matchupBuckets.strong.map((type) => (
                      <PokemonTypeTag key={`strong-${type}`} type={type} className="rounded-full shadow-sm" />
                    ))}
                  </div>
                </div>

                <div className="rounded-[1.25rem] border border-amber-100 bg-amber-50/70 p-4">
                  <div className="flex items-center gap-2 text-amber-700">
                    <Shield className="h-4 w-4" />
                    <h3 className="text-sm font-bold">{copy.weakTitle}</h3>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {matchupBuckets.weak.map((type) => (
                      <PokemonTypeTag key={`weak-${type}`} type={type} className="rounded-full shadow-sm" />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="grid gap-6 xl:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
            <div className="type-chart-fade-up rounded-[1.75rem] border border-white/80 bg-white/88 p-5 shadow-[0_30px_70px_-46px_rgba(15,23,42,0.38)] backdrop-blur-sm">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-pokemon-blue">
                    {copy.analyzerTitle}
                  </p>
                  <h2 className="mt-2 text-2xl font-black text-slate-900">{copy.resultTitle}</h2>
                  <p className="mt-2 text-sm leading-7 text-slate-600">{copy.analyzerBody}</p>
                </div>
                <div className="rounded-full bg-pokemon-blue/5 p-2 text-pokemon-blue">
                  <Crosshair className="h-5 w-5" />
                </div>
              </div>

              <div className="mt-6 space-y-5">
                <div>
                  <p className="mb-3 text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">
                    {copy.attackLabel}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {POKEMON_TYPES.map((type) => (
                      <button
                        key={`attack-${type}`}
                        type="button"
                        onClick={() => setSelectedAttack(type)}
                        className={cn(
                          "rounded-full border px-1 py-1 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md",
                          selectedAttack === type
                            ? "border-pokemon-blue/25 bg-pokemon-blue/10 ring-2 ring-pokemon-blue/10"
                            : "border-transparent bg-transparent"
                        )}
                      >
                        <PokemonTypeTag type={type} className="rounded-full px-3 py-1.5 text-sm shadow-sm" />
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid gap-5 md:grid-cols-2">
                  <div>
                    <p className="mb-3 text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">
                      {copy.defenseLabel}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {POKEMON_TYPES.map((type) => (
                        <button
                          key={`defense-${type}`}
                          type="button"
                          onClick={() => setSelectedDefense(type)}
                          className={cn(
                            "rounded-full border px-1 py-1 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md",
                            selectedDefense === type
                              ? "border-pokemon-blue/25 bg-pokemon-blue/10 ring-2 ring-pokemon-blue/10"
                              : "border-transparent bg-transparent"
                          )}
                        >
                          <PokemonTypeTag type={type} className="rounded-full px-3 py-1.5 text-sm shadow-sm" />
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="mb-3 text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">
                      {copy.secondaryLabel}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => setSelectedSecondaryDefense("none")}
                        className={cn(
                          "rounded-full border px-3 py-2 text-sm font-medium transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md",
                          selectedSecondaryDefense === "none"
                            ? "border-slate-900 bg-slate-900 text-white"
                            : "border-slate-200 bg-white text-slate-600"
                        )}
                      >
                        {copy.secondaryPlaceholder}
                      </button>

                      {POKEMON_TYPES.map((type) => (
                        <button
                          key={`secondary-${type}`}
                          type="button"
                          onClick={() => setSelectedSecondaryDefense(type)}
                          className={cn(
                            "rounded-full border px-1 py-1 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md",
                            selectedSecondaryDefense === type
                              ? "border-pokemon-blue/25 bg-pokemon-blue/10 ring-2 ring-pokemon-blue/10"
                              : "border-transparent bg-transparent"
                          )}
                        >
                          <PokemonTypeTag type={type} className="rounded-full px-3 py-1.5 text-sm shadow-sm" />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="rounded-[1.2rem] border border-slate-200 bg-slate-50/70 p-4">
                    <h3 className="text-sm font-bold text-slate-900">{copy.immuneTitle}</h3>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {matchupBuckets.immune.map((type) => (
                        <PokemonTypeTag key={`immune-${type}`} type={type} className="rounded-full shadow-sm" />
                      ))}
                    </div>
                  </div>

                  <div className="rounded-[1.2rem] border border-slate-200 bg-slate-50/70 p-4">
                    <h3 className="text-sm font-bold text-slate-900">{copy.neutralTitle}</h3>
                    <p className="mt-2 text-2xl font-black text-slate-900">{matchupBuckets.neutral.length}</p>
                    <p className="mt-1 text-sm text-slate-500">1x</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="type-chart-fade-up-delayed rounded-[1.75rem] border border-white/80 bg-white/88 p-5 shadow-[0_30px_70px_-46px_rgba(15,23,42,0.38)] backdrop-blur-sm">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.22em] text-pokemon-blue">
                      {viewMode === "matrix" ? copy.matrixTitle : copy.radarTitle}
                    </p>
                    <h2 className="mt-2 text-2xl font-black text-slate-900">{copy.title}</h2>
                    <p
                      className={cn(
                        "mt-2 max-w-2xl text-slate-600",
                        viewMode === "matrix" ? "text-sm leading-7" : "text-xs leading-6 sm:text-sm"
                      )}
                    >
                      {viewMode === "matrix" ? copy.matrixBody : copy.radarBody}
                    </p>
                  </div>
                <div className="flex flex-wrap items-center gap-2">
                  <div className="inline-flex rounded-full border border-slate-200 bg-white p-1 shadow-sm">
                    <button
                      type="button"
                      onClick={() => setViewMode("matrix")}
                      className={cn(
                        "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition",
                        viewMode === "matrix" ? "bg-slate-900 text-white" : "text-slate-500"
                      )}
                    >
                      <Grid2x2 className="h-3.5 w-3.5" />
                      {copy.matrixView}
                    </button>
                    <button
                      type="button"
                      onClick={() => setViewMode("radar")}
                      className={cn(
                        "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition",
                        viewMode === "radar" ? "bg-slate-900 text-white" : "text-slate-500"
                      )}
                    >
                      <Orbit className="h-3.5 w-3.5" />
                      {copy.radarView}
                    </button>
                  </div>
                  <div
                    className={cn(
                      "rounded-full border border-slate-200 bg-white px-3 py-1 text-slate-500",
                      viewMode === "matrix" ? "text-xs" : "text-[11px]"
                    )}
                  >
                    {viewMode === "matrix" ? copy.hoverHint : copy.radarHoverHint}
                  </div>
                </div>
              </div>

              {viewMode === "matrix" ? (
                <div className="mt-5 overflow-x-auto pb-2">
                  <div className="min-w-[860px]">
                    <div className="grid grid-cols-[110px_repeat(18,minmax(0,1fr))] gap-2">
                        <div
                          className="flex items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-slate-400"
                        >
                          {copy.attackHeader}/{copy.defenseHeader}
                        </div>
                        {POKEMON_TYPES.map((defenseType) => (
                          <div
                            key={`defense-header-${defenseType}`}
                            className={cn(
                              "flex items-center justify-center rounded-2xl border px-2 py-2 transition-all duration-200",
                              activeCell.defense === defenseType
                                ? "border-pokemon-blue/25 bg-pokemon-blue/10"
                                : "border-slate-100 bg-slate-50/70"
                            )}
                          >
                            <PokemonTypeTag type={defenseType} className="rounded-full px-2.5 py-1 text-xs shadow-sm" />
                          </div>
                        ))}

                        {POKEMON_TYPES.map((attackType) => (
                          <Fragment key={`matrix-row-${attackType}`}>
                            <div
                              key={`attack-header-${attackType}`}
                              className={cn(
                                "flex items-center justify-center rounded-2xl border px-2 py-2 transition-all duration-200",
                                activeCell.attack === attackType
                                  ? "border-pokemon-blue/25 bg-pokemon-blue/10"
                                  : "border-slate-100 bg-slate-50/70"
                              )}
                            >
                              <PokemonTypeTag type={attackType} className="rounded-full px-2.5 py-1 text-xs shadow-sm" />
                            </div>

                            {POKEMON_TYPES.map((defenseType) => {
                              const multiplier = getTypeEffectiveness(attackType, defenseType);
                              const isFocused =
                                activeCell.attack === attackType || activeCell.defense === defenseType;
                              const isSelected =
                                activeCell.attack === attackType && activeCell.defense === defenseType;

                              return (
                                <button
                                  key={`${attackType}-${defenseType}`}
                                  type="button"
                                  onMouseEnter={() => setHoveredCell({ attack: attackType, defense: defenseType })}
                                  onMouseLeave={() => setHoveredCell(null)}
                                  onFocus={() => setHoveredCell({ attack: attackType, defense: defenseType })}
                                  onBlur={() => setHoveredCell(null)}
                                  onClick={() => setPinnedCell({ attack: attackType, defense: defenseType })}
                                  className={cn(
                                    "type-chart-cell group relative flex h-14 items-center justify-center overflow-hidden rounded-2xl border text-sm font-black tracking-tight transition-all duration-200",
                                    getMultiplierTone(multiplier),
                                    isFocused && "scale-[1.01]",
                                    isSelected && "ring-2 ring-pokemon-blue/20",
                                    !isFocused && "opacity-80"
                                  )}
                                >
                                  <span className="relative z-10">{formatMultiplier(multiplier)}</span>
                                  <span
                                    className={cn(
                                      "pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100",
                                      multiplier > 1
                                        ? "bg-[linear-gradient(135deg,rgba(12,96,179,0.18),transparent_55%)]"
                                        : multiplier < 1
                                          ? "bg-[linear-gradient(135deg,rgba(217,119,6,0.18),transparent_55%)]"
                                          : "bg-[linear-gradient(135deg,rgba(148,163,184,0.14),transparent_55%)]"
                                    )}
                                  />
                                </button>
                              );
                            })}
                          </Fragment>
                        ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mt-4">
                  <div className="relative overflow-hidden rounded-[1.85rem] border border-slate-200/80 bg-[radial-gradient(circle_at_center,_rgba(255,255,255,0.98),_rgba(239,246,255,0.96)_48%,_rgba(248,250,252,0.98)_100%)] p-6 sm:p-8 xl:px-24 xl:py-16">
                    <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(12,96,179,0.10),_transparent_42%)]" />
                    <div className="pointer-events-none absolute inset-0 opacity-70">
                      <div className="radar-sweep absolute left-1/2 top-1/2 h-[34rem] w-[34rem] -translate-x-1/2 -translate-y-1/2 rounded-full" />
                    </div>
                    <div className="pointer-events-none absolute inset-x-0 top-0 h-32 bg-[linear-gradient(180deg,_rgba(255,255,255,0.42),_transparent)]" />

                    <div className="relative hidden xl:block">
                      <div className="pointer-events-auto absolute left-1/2 top-4 z-10 flex -translate-x-1/2 items-center gap-3 rounded-full border border-white/80 bg-white/86 px-4 py-2 shadow-[0_18px_40px_-30px_rgba(15,23,42,0.18)] backdrop-blur-sm">
                        <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                          {copy.radarFocusTitle}
                        </span>
                        <PokemonTypeTag
                          type={focusedRadarNode.type}
                          className="rounded-full px-3 py-1 text-xs shadow-sm"
                        />
                        <span className="rounded-full bg-slate-900 px-2 py-1 text-[10px] font-bold text-white">
                          {formatMultiplier(focusedRadarNode.multiplier)}
                        </span>
                        <span className="text-[11px] text-slate-500">
                          {copy.radarCurrentAttack}
                        </span>
                        <span className="text-sm font-semibold text-slate-700">{t(selectedAttack)}</span>
                      </div>

                      <div className="pointer-events-none absolute left-1/2 top-[5.4rem] z-10 flex -translate-x-1/2 items-center gap-4 rounded-full border border-white/70 bg-white/72 px-4 py-2 text-[11px] text-slate-500 shadow-[0_18px_40px_-34px_rgba(15,23,42,0.14)] backdrop-blur-sm">
                        <span className="inline-flex items-center gap-2">
                          <span className="h-2 w-2 rounded-full bg-sky-400" />
                          {copy.radarStrongRing}
                        </span>
                        <span className="inline-flex items-center gap-2">
                          <span className="h-2 w-2 rounded-full bg-slate-300" />
                          {copy.radarNeutralRing}
                        </span>
                        <span className="inline-flex items-center gap-2">
                          <span className="h-2 w-2 rounded-full bg-amber-300" />
                          {copy.radarWeakRing}
                        </span>
                        <span className="inline-flex items-center gap-2">
                          <span className="h-2 w-2 rounded-full bg-slate-900" />
                          {copy.radarImmuneRing}
                        </span>
                      </div>
                    </div>

                    <div
                      ref={radarCanvasRef}
                      onMouseMove={handleRadarPointerMove}
                      onMouseLeave={handleRadarPointerLeave}
                      className="scrollbar-hidden flex items-center justify-center overflow-x-auto overflow-y-hidden"
                    >
                      <svg
                        viewBox="-300 -300 600 600"
                        className="h-[36rem] w-[36rem] min-w-[30rem] transition-transform duration-300 ease-out"
                        style={{ transform: radarStageTransform }}
                        role="img"
                        aria-label={copy.radarTitle}
                      >
                        <defs>
                          <radialGradient id="radarGlow" cx="50%" cy="50%" r="50%">
                            <stop offset="0%" stopColor="rgba(12,96,179,0.14)" />
                            <stop offset="100%" stopColor="rgba(12,96,179,0)" />
                          </radialGradient>
                          <linearGradient id="radarPathStrong" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="rgba(12,96,179,0.04)" />
                            <stop offset="50%" stopColor="rgba(12,96,179,0.42)" />
                            <stop offset="100%" stopColor="rgba(12,96,179,0.04)" />
                          </linearGradient>
                          <linearGradient id="radarPathWeak" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="rgba(217,119,6,0.04)" />
                            <stop offset="50%" stopColor="rgba(217,119,6,0.34)" />
                            <stop offset="100%" stopColor="rgba(217,119,6,0.04)" />
                          </linearGradient>
                          <linearGradient id="radarPathNeutral" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="rgba(148,163,184,0.03)" />
                            <stop offset="50%" stopColor="rgba(148,163,184,0.28)" />
                            <stop offset="100%" stopColor="rgba(148,163,184,0.03)" />
                          </linearGradient>
                          <linearGradient id="radarPathImmune" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="rgba(15,23,42,0.05)" />
                            <stop offset="50%" stopColor="rgba(15,23,42,0.34)" />
                            <stop offset="100%" stopColor="rgba(15,23,42,0.05)" />
                          </linearGradient>
                          <filter id="radarSoftGlow">
                            <feGaussianBlur stdDeviation="8" result="blur" />
                            <feMerge>
                              <feMergeNode in="blur" />
                              <feMergeNode in="SourceGraphic" />
                            </feMerge>
                          </filter>
                        </defs>

                        <circle r="244" fill="url(#radarGlow)" />
                        <circle
                          r={RADAR_RINGS.strong + 18}
                          fill="none"
                          stroke="rgba(12,96,179,0.08)"
                        />
                        <circle r={RADAR_RINGS.strong} fill="none" stroke="rgba(12,96,179,0.16)" strokeDasharray="6 8" />
                        <circle r={RADAR_RINGS.neutral} fill="none" stroke="rgba(148,163,184,0.24)" strokeDasharray="6 10" />
                        <circle r={RADAR_RINGS.weak} fill="none" stroke="rgba(217,119,6,0.18)" strokeDasharray="5 10" />
                        <circle r={RADAR_RINGS.immune} fill="none" stroke="rgba(15,23,42,0.22)" strokeDasharray="4 8" />
                        <circle r="18" fill="rgba(12,96,179,0.08)" />
                        <circle
                          r={
                            highlightedRing === "strong"
                              ? RADAR_RINGS.strong
                              : highlightedRing === "weak"
                                ? RADAR_RINGS.weak
                                : highlightedRing === "immune"
                                  ? RADAR_RINGS.immune
                                  : RADAR_RINGS.neutral
                          }
                          fill="none"
                          stroke={
                            highlightedRing === "strong"
                              ? "rgba(12,96,179,0.3)"
                              : highlightedRing === "weak"
                                ? "rgba(217,119,6,0.28)"
                                : highlightedRing === "immune"
                                  ? "rgba(15,23,42,0.32)"
                                  : "rgba(148,163,184,0.32)"
                          }
                          strokeWidth="8"
                          opacity={radarPointer.active ? 0.22 : 0.12}
                          filter="url(#radarSoftGlow)"
                          className="transition-all duration-300"
                        />
                        <path
                          d={`
                            M 0 ${-(RADAR_RINGS.strong + 18)}
                            A ${RADAR_RINGS.strong + 18} ${RADAR_RINGS.strong + 18} 0 0 1 ${RADAR_RINGS.strong + 18} 0
                          `}
                          fill="none"
                          stroke="rgba(12,96,179,0.22)"
                          strokeWidth="3"
                          strokeLinecap="round"
                          className="radar-orbit-arc"
                        >
                          <animateTransform
                            attributeName="transform"
                            type="rotate"
                            from="0 0 0"
                            to="360 0 0"
                            dur="10s"
                            repeatCount="indefinite"
                          />
                          <animate
                            attributeName="opacity"
                            values="0.15;0.55;0.15"
                            dur="2.8s"
                            repeatCount="indefinite"
                          />
                        </path>

                        {radarNodes.map((node) => {
                          const isSelected = selectedDefense === node.type;
                          const isFocused = focusedRadarType === node.type;
                          const distanceToPointer = Math.sqrt(
                            Math.pow(radarPointer.x - node.x, 2) + Math.pow(radarPointer.y - node.y, 2)
                          );
                          const pointerInfluence = radarPointer.active
                            ? Math.max(0, 1 - distanceToPointer / 140)
                            : 0;
                          const shiftedX =
                            node.x + (radarPointer.active ? ((radarPointer.x - node.x) / 44) * pointerInfluence : 0);
                          const shiftedY =
                            node.y + (radarPointer.active ? ((radarPointer.y - node.y) / 44) * pointerInfluence : 0);
                          const stroke =
                            node.multiplier === 0
                              ? "url(#radarPathImmune)"
                              : node.multiplier > 1
                                ? "url(#radarPathStrong)"
                                : node.multiplier < 1
                                  ? "url(#radarPathWeak)"
                                  : "url(#radarPathNeutral)";
                          const glowStroke =
                            node.multiplier === 0
                              ? "rgba(15,23,42,0.22)"
                              : node.multiplier > 1
                                ? "rgba(12,96,179,0.2)"
                                : node.multiplier < 1
                                  ? "rgba(217,119,6,0.18)"
                                  : "rgba(148,163,184,0.18)";
                          const pathLength = Math.sqrt(shiftedX * shiftedX + shiftedY * shiftedY);
                          const motionPath = `M 0 0 Q ${shiftedX * 0.38} ${shiftedY * 0.38} ${shiftedX} ${shiftedY}`;

                          return (
                            <g key={`radar-node-${node.type}`}>
                              <line
                                x1={0}
                                y1={0}
                                x2={shiftedX}
                                y2={shiftedY}
                                stroke={glowStroke}
                                strokeWidth={isFocused ? 4.5 : isSelected ? 3.6 : 2.6}
                                opacity={isFocused || isSelected ? 0.4 : 0.16}
                                filter="url(#radarSoftGlow)"
                              />
                              <path
                                d={motionPath}
                                stroke={stroke}
                                strokeWidth={isFocused ? 2.5 : isSelected ? 2 : 1}
                                opacity={isFocused || isSelected ? 1 : 0.5}
                                strokeDasharray={pathLength}
                                strokeDashoffset={pathLength}
                                className={isFocused || isSelected ? "radar-link-active" : "radar-link-idle"}
                                fill="none"
                                strokeLinecap="round"
                              >
                                <animate
                                  key={`draw-${radarAnimationSeed}-${node.type}`}
                                  attributeName="stroke-dashoffset"
                                  from={pathLength}
                                  to="0"
                                  dur={isFocused || isSelected ? "0.55s" : "0.85s"}
                                  fill="freeze"
                                />
                                {isFocused || isSelected ? (
                                  <animate
                                    attributeName="opacity"
                                    values="0.65;1;0.82"
                                    dur="1.8s"
                                    repeatCount="indefinite"
                                  />
                                ) : null}
                              </path>
                              {isFocused ? (
                                <circle
                                  r="6"
                                  fill="rgba(255,255,255,0.95)"
                                  filter="url(#radarSoftGlow)"
                                  className="radar-node-pulse"
                                />
                              ) : null}
                            </g>
                          );
                        })}

                        {radarNodes.map((node) => {
                          const isSelected = selectedDefense === node.type;
                          const isFocused = focusedRadarType === node.type;
                          const distanceToPointer = Math.sqrt(
                            Math.pow(radarPointer.x - node.x, 2) + Math.pow(radarPointer.y - node.y, 2)
                          );
                          const pointerInfluence = radarPointer.active
                            ? Math.max(0, 1 - distanceToPointer / 140)
                            : 0;
                          const shiftedX =
                            node.x + (radarPointer.active ? ((radarPointer.x - node.x) / 44) * pointerInfluence : 0);
                          const shiftedY =
                            node.y + (radarPointer.active ? ((radarPointer.y - node.y) / 44) * pointerInfluence : 0);

                          return (
                            <g key={node.type} transform={`translate(${shiftedX} ${shiftedY})`}>
                              {isFocused ? (
                                <circle
                                  r="30"
                                  fill="rgba(12,96,179,0.08)"
                                  filter="url(#radarSoftGlow)"
                                  className="radar-node-pulse"
                                />
                              ) : null}
                              <foreignObject x={-36} y={-20} width="72" height="40">
                                <button
                                  type="button"
                                  onClick={() => setSelectedDefense(node.type)}
                                  onMouseEnter={() => setHoveredRadarType(node.type)}
                                  onMouseLeave={() => setHoveredRadarType(null)}
                                  onFocus={() => setHoveredRadarType(node.type)}
                                  onBlur={() => setHoveredRadarType(null)}
                                  className={cn(
                                    "flex h-10 w-[72px] items-center justify-center rounded-full border bg-white/92 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-lg",
                                    getMultiplierTone(node.multiplier),
                                    isFocused && "scale-[1.04]",
                                    isSelected && "ring-2 ring-pokemon-blue/25"
                                  )}
                                >
                                  <span className="text-[11px] font-black tracking-tight">{t(node.type)}</span>
                                </button>
                              </foreignObject>
                              {(isFocused || isSelected) && node.multiplier !== 1 ? (
                                <foreignObject x={-19} y={18} width="38" height="20">
                                  <div className="flex h-5 items-center justify-center rounded-full bg-slate-900/88 text-[10px] font-bold text-white shadow-sm">
                                    {formatMultiplier(node.multiplier)}
                                  </div>
                                </foreignObject>
                              ) : null}
                            </g>
                          );
                        })}

                        <g>
                          <circle r="52" fill="white" stroke="rgba(12,96,179,0.16)" strokeWidth="2" />
                          <circle r="38" fill="rgba(12,96,179,0.08)" filter="url(#radarSoftGlow)" />
                          <foreignObject x={-56} y={-24} width="112" height="48">
                            <div className="flex h-12 items-center justify-center">
                              <PokemonTypeTag type={selectedAttack} className="rounded-full px-4 py-2 text-sm shadow-sm" />
                            </div>
                          </foreignObject>
                        </g>
                      </svg>
                    </div>

                    <div className="mt-4 grid gap-2.5 xl:hidden">
                      <div className="rounded-[1.25rem] border border-white/80 bg-white/88 p-3.5 shadow-sm">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                          {copy.radarFocusTitle}
                        </p>
                        <div className="mt-2 flex items-center justify-between gap-2">
                          <PokemonTypeTag
                            type={focusedRadarNode.type}
                            className="rounded-full px-3 py-1.5 text-sm shadow-sm"
                          />
                          <span className="rounded-full bg-slate-900 px-2 py-1 text-[10px] font-bold text-white">
                            {formatMultiplier(focusedRadarNode.multiplier)}
                          </span>
                        </div>
                        <div className="mt-3 space-y-2 text-[11px] text-slate-500">
                          <div className="flex items-center justify-between gap-2">
                            <span>{copy.radarCurrentAttack}</span>
                            <span className="font-semibold text-slate-700">{t(selectedAttack)}</span>
                          </div>
                          <div className="flex items-center justify-between gap-2">
                            <span>{copy.radarMultiplier}</span>
                            <span className="font-semibold text-slate-700">{formatMultiplier(focusedRadarNode.multiplier)}</span>
                          </div>
                          <div className="flex items-center justify-between gap-2">
                            <span>{copy.radarInteraction}</span>
                            <span className="font-semibold text-slate-700">
                              {focusedRadarNode.type === selectedDefense
                                ? copy.radarInteractionPinned
                                : copy.radarInteractionPreview}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                        <div className="rounded-[1.2rem] border border-sky-100 bg-sky-50/70 p-3">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-sky-700">
                            {copy.radarStrongRing}
                          </p>
                          <p className="mt-1 text-[11px] text-slate-600">{copy.radarLegendStrongText}</p>
                        </div>
                        <div className="rounded-[1.2rem] border border-slate-200 bg-slate-50/80 p-3">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-700">
                            {copy.radarNeutralRing}
                          </p>
                          <p className="mt-1 text-[11px] text-slate-600">{copy.radarLegendNeutralText}</p>
                        </div>
                        <div className="rounded-[1.2rem] border border-amber-100 bg-amber-50/70 p-3">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-amber-700">
                            {copy.radarWeakRing}
                          </p>
                          <p className="mt-1 text-[11px] text-slate-600">{copy.radarLegendWeakText}</p>
                        </div>
                        <div className="rounded-[1.2rem] border border-slate-700/10 bg-slate-900 p-3 text-white">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-300">
                            {copy.radarImmuneRing}
                          </p>
                          <p className="mt-1 text-[11px] text-slate-300">{copy.radarLegendImmuneText}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
    </MainLayout>
  );
};

export default TypeChartPage;
